import React, { useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import { InvestStatementPanel, InvestmentInfoPanel } from 'client/investNew/_components';
import TradeOperationsTable from 'client/_components/TradeOperationsTable';

import {
  PageLayout,
  PageContentPreloader,
  PageTabs,
  TranslatedNavLink,
  Calendar,
  NoItemsPanel,
} from 'common/components';
import { withTheme } from 'common/styling/theme';
import noOrdersImage from 'common/images/noOrders.svg';

import { getBackofficeInvestStatementNew, getBackofficeInvestmentTradesNew } from '../../_redux';
import { staticStyles, getDynamicStyles } from './style';

const startDate = moment().startOf('day').unix();

const endDate = moment().endOf('day').unix();

const startCalendar = moment().startOf('day');

const endCalendar = moment().endOf('day');

const PammInvestorAccountPage = ({
  investStatement,
  investStatementIsLoaded,
  investmentTrades,
  investmentTradesAreLoaded,
  getInvestStatement,
  getInvestmentTrades,
  theme,
}) => {
  const match = useRouteMatch();
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const {
    params: { userId, investmentId, tradeType },
  } = match;

  const handleChangeDate = (start, end) => {
    getInvestmentTrades({ investmentId, tradeType, userId, startDate: start, endDate: end });
  };

  useEffect(() => {
    getInvestStatement({ userId, investmentId });
  }, [getInvestStatement, userId, investmentId]);

  useEffect(() => {
    getInvestmentTrades({ userId, investmentId, tradeType, startDate, endDate });
  }, [getInvestmentTrades, userId, investmentId, tradeType]);

  return (
    <PageLayout
      captionSlug="boPammInvestorAccount"
      captionValues={{ account: String(investmentId) }}
      showReload={false}>
      <PageContentPreloader ready={investStatementIsLoaded} type="bigLine" firstLaunchOnly>
        <InvestStatementPanel investStatement={investStatement} />
        <InvestmentInfoPanel investStatement={investStatement} />
        <>
          <div className="PammInvestorAccountPage__top-line">
            <PageTabs>
              <TranslatedNavLink
                id="justOpen"
                to={`/backoffice/users/${userId}/investments_crm/${investmentId}/open_trades`}
              />

              <TranslatedNavLink
                id="justClosed"
                to={`/backoffice/users/${userId}/investments_crm/${investmentId}/closed_trades`}
              />
            </PageTabs>
            <div className="PammInvestorAccountPage__date">
              {tradeType === 'closed_trades' && (
                <Calendar
                  daysLimit={30}
                  onChangeFunc={handleChangeDate}
                  defaultStartDate={startCalendar}
                  defaultEndDate={endCalendar}
                  setCorrectLastDay
                />
              )}
            </div>
          </div>
          <div className="PammInvestorAccountPage__table">
            <PageContentPreloader ready={investmentTradesAreLoaded} type="smallLines">
              {investmentTrades.items?.length ? (
                <TradeOperationsTable
                  data={investmentTrades.items}
                  summary={investmentTrades.summary}
                  operationStatus={tradeType}
                />
              ) : (
                <NoItemsPanel icon={noOrdersImage} captionSlug="yotHaveNoOrdersYet" />
              )}
            </PageContentPreloader>
          </div>
        </>
      </PageContentPreloader>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </PageLayout>
  );
};

PammInvestorAccountPage.propTypes = {
  investStatement: PropTypes.object.isRequired,
  investStatementIsLoaded: PropTypes.bool.isRequired,
  investmentTrades: PropTypes.object.isRequired,
  investmentTradesAreLoaded: PropTypes.bool.isRequired,
  getInvestStatement: PropTypes.func.isRequired,
  getInvestmentTrades: PropTypes.func.isRequired,
  theme: PropTypes.object,
};

PammInvestorAccountPage.defaultProps = {
  theme: {},
};

export default compose(
  withTheme(),
  connect(
    state => ({
      investStatement: state.backoffice.users.backofficeInvestStatement,
      investStatementIsLoaded: state.backoffice.users.backofficeInvestStatementIsLoaded,
      investmentTrades: state.backoffice.users.backofficeInvestmentTrades,
      investmentTradesAreLoaded: state.backoffice.users.backofficeInvestmentTradesAreLoaded,
    }),
    {
      getInvestStatement: data => getBackofficeInvestStatementNew.request(data),
      getInvestmentTrades: data => getBackofficeInvestmentTradesNew.request(data),
    }
  )
)(PammInvestorAccountPage);
export { PammInvestorAccountPage };
